import React, { useState, useEffect, useRef } from 'react';
import { getNotificationCount, getNewNotifications } from '../services/notificationService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './NotificationDropdown.module.css';

const NotificationDropdown = ({ onShowAll }) => {
    const { hasShowNotification } = useCachedPermissions();

    // Call all hooks first to comply with React hooks rules
    const [isOpen, setIsOpen] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch notification count on component mount
    useEffect(() => {
        // Only fetch if user has permission
        if (hasShowNotification) {
            fetchNotificationCount();
            // Set up interval to periodically check for new notifications
            const interval = setInterval(fetchNotificationCount, ((60000) * 3)); // Check every 60 seconds
            return () => clearInterval(interval);
        }
    }, [hasShowNotification]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const fetchNotificationCount = async () => {
        try {
            const response = await getNotificationCount();
            setNotificationCount(response.data.count);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    };

    const fetchNewNotifications = async () => {
        if (loading) return;

        setLoading(true);
        try {
            const response = await getNewNotifications(1, 5); // Get first 5 notifications for dropdown
            setNotifications(response.data.data);
        } catch (error) {
            console.error('Error fetching new notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDropdownToggle = () => {
        if (!isOpen) {
            fetchNewNotifications();
        }
        setIsOpen(!isOpen);
    };

    const handleShowAll = () => {
        setIsOpen(false);
        if (onShowAll) {
            onShowAll();
        }
    };

    // If user doesn't have notification permission, don't render
    if (!hasShowNotification) {
        return null;
    }

    return (
        <div className={styles.notificationWrapper} ref={dropdownRef}>
            <button
                className={styles.notificationButton}
                onClick={handleDropdownToggle}
                aria-label="Notifications"
            >
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {notificationCount > 0 && (
                    <span className={styles.notificationBadge}>
                        {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className={styles.notificationDropdown}>
                    <div className={styles.dropdownHeader}>
                        <span>New Notifications</span>
                        {notificationCount > 0 && (
                            <span className={styles.countBadge}>{notificationCount}</span>
                        )}
                    </div>

                    <div className={styles.notificationList}>
                        {loading ? (
                            <div className={styles.loadingState}>
                                <div className={styles.loadingSpinner}></div>
                                <span>Loading notifications...</span>
                            </div>
                        ) : notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <div key={notification.id} className={styles.notificationItem}>
                                    <div className={styles.notificationIcon}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 6v6l4 2" />
                                        </svg>
                                    </div>
                                    <div className={styles.notificationContent}>
                                        <p className={styles.notificationMessage}>
                                            {notification.message}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.emptyState}>
                                <p>No new notifications</p>
                            </div>
                        )}
                    </div>

                    <div className={styles.dropdownFooter}>
                        <button
                            className={styles.showAllButton}
                            onClick={handleShowAll}
                        >
                            Show All Notifications
                        </button>
                    </div>

                    {/* {notifications.length > 0 && (
                        
                    )} */}
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
